import * as React from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { LatLngBounds, divIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { cn } from '@/shared/lib/utils';
import { buildingType } from '@/entities/knowledge/model/types';
import { Button } from '@/shared/ui/Button';
import { KnowledgeMapCard } from '@/shared/ui/Card/KnowledgeMapCard';

export interface IMapProps {
  buildings: buildingType[];
  className?: string;
}

const FitBounds = ({ points }: { points: [number, number][] }) => {
  const map = useMap();

  React.useEffect(() => {
    if (points.length === 0) return;

    const bounds = new LatLngBounds(points);
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [points, map]);

  return null;
};

const createCustomIcon = (name: string, color: string) => {
  return divIcon({
    className: 'custom-div-icon',
    html: `
            <div class="relative flex flex-col items-center">
                
                <!-- Название -->
                <div class="absolute -top-8 text-[10px] uppercase font-medium whitespace-nowrap
                    px-[12px] py-[6px] rounded-[10px] bg-white text-black shadow-lg z-10">
                    ${name}
                </div>

                <!-- Треугольник -->
                <div class="absolute -top-[6px] w-0 h-0 
                    border-l-8 border-l-transparent 
                    border-r-8 border-r-transparent 
                    border-t-8 border-t-white z-0">
                </div>

                <!-- Большая точка -->
                <div class="w-[15px] h-[15px] rounded-full border-[3px] border-white shadow-lg" style="background-color: ${color};"></div>
            </div>
        `,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });
};

export const addtionalBuildingTypes = {
  commercial: { id: 'commercial', name: 'Бизнес Центры', color: '#4450BC' },
  retail: { id: 'retail', name: 'Ритейл в ЖК', color: '#258444' },
  residential: {
    id: 'residential',
    name: 'Отдельностоящие здания',
    color: '#B252B2',
  },
} as const;

export function Map({ buildings, className }: IMapProps) {
  const defaultCenter: [number, number] =
    buildings.length > 0 ? buildings[0].coordinates : [51.505, -0.09];
  const [selectedBuildingTypes, setSelectedBuildingTypes] = React.useState<
    Record<string, boolean>
  >({
    commercial: true,
    retail: true,
    residential: true,
  });

  const handleBuildingType = (key: string) => {
    setSelectedBuildingTypes((prevHouseTypes) => ({
      ...prevHouseTypes,
      [key]: !prevHouseTypes[key],
    }));
  };

  return (
    <div className="relative w-full overflow-hidden rounded-2xl">
      <div className=" absolute right-[2px] top-[10px] z-10 flex gap-[4px] rounded-[10px] bg-white p-[4px] md:gap-[8px]">
        {Object.values(addtionalBuildingTypes).map((item, index) => (
          <Button
            key={index}
            text={item.name}
            className="rounded-[10px] p-[8px] text-[12px] font-normal leading-[0.95em] text-white"
            style={{
              backgroundColor: selectedBuildingTypes[item.id]
                ? item.color
                : 'grey',
            }}
            onClick={() => handleBuildingType(item.id)}
          />
        ))}
      </div>

      <div
        className={cn('relative z-0 h-[500px] w-full lg:h-[600px]', className)}
      >
        <MapContainer
          center={defaultCenter}
          zoom={14}
          scrollWheelZoom={false}
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <FitBounds points={buildings.map((p) => p.coordinates)} />

          {buildings
            .filter((item) => selectedBuildingTypes[item.direction])
            .map((building, index) => {
              const additional =
                addtionalBuildingTypes[
                  building.direction as keyof typeof addtionalBuildingTypes
                ];

              return (
                <Marker
                  key={index}
                  position={building.coordinates}
                  icon={createCustomIcon(building.name, additional.color)}
                >
                  <Popup className="p-0">
                    <KnowledgeMapCard
                      building={building}
                      inMap={true}
                      additional={additional}
                    />
                  </Popup>
                </Marker>
              );
            })}
        </MapContainer>
      </div>
    </div>
  );
}
